import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Row, Col, Modal, Button, Spinner } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaRupeeSign,
  FaCheckCircle,
  FaArrowRight,
  FaChevronRight,
  FaLock,
  FaTimes,
  FaGlobe,
  FaChevronDown,
  FaCheck,
  FaBoxes,
  FaBan,
  FaExclamationTriangle,
  FaTimesCircle,
  FaFileDownload,
  FaClipboardList,
} from "react-icons/fa";
import { toast } from "react-toastify";
import SubmitQuoteModal from "./SubmitQuoteModal";
import CompoLoader from "../ComponentLoader/CompoLoader";
import "./networkhub.scss";
import BackButton from "../Common/BackButton/BackButton";
import { getCountryListAPiCall } from "../../Services/CommonFunction";
import WebService from "../../Services/WebService";
import Logo from "../../assets/images/logo.svg";
import moment from "moment";

const getCategoryIcon = (category: string) => {
  const cat = (category || "").toLowerCase();
  if (cat.includes("agri") || cat.includes("farm") || cat.includes("wheat") || cat.includes("grain"))
    return { icon: "🌾", iconBg: "#fef3c7" };
  if (
    cat.includes("construct") ||
    cat.includes("steel") ||
    cat.includes("pipe") ||
    cat.includes("cement") ||
    cat.includes("building")
  )
    return { icon: "🏗️", iconBg: "#e0f2fe" };
  if (cat.includes("chem") || cat.includes("pharma") || cat.includes("medical") || cat.includes("drug"))
    return { icon: "🧪", iconBg: "#f0fdf4" };
  if (
    cat.includes("electr") ||
    cat.includes("chip") ||
    cat.includes("tech") ||
    cat.includes("device") ||
    cat.includes("circuit")
  )
    return { icon: "🔌", iconBg: "#e0e7ff" };
  if (cat.includes("textil") || cat.includes("cotton") || cat.includes("fabric") || cat.includes("yarn") || cat.includes("apparel"))
    return { icon: "🧵", iconBg: "#ffe4e6" };
  if (cat.includes("solar") || cat.includes("renew") || cat.includes("energy") || cat.includes("power"))
    return { icon: "☀️", iconBg: "#fef9c3" };
  if (cat.includes("health") || cat.includes("hospital") || cat.includes("clinic"))
    return { icon: "🩺", iconBg: "#e0f2fe" };
  if (cat.includes("food") || cat.includes("bever") || cat.includes("drink") || cat.includes("dairy"))
    return { icon: "🍔", iconBg: "#fed7aa" };
  if (cat.includes("auto") || cat.includes("car") || cat.includes("vehicle") || cat.includes("motor"))
    return { icon: "🚗", iconBg: "#fee2e2" };
  return { icon: "📦", iconBg: "#f1f5f9" };
};

const getCountryFlag = (countryName: string, countryOptions: any[]) => {
  if (!countryName) return null;
  const match = countryOptions.find(
    (c: any) =>
      (c?.title || c?.name || c?.country_name || (typeof c === "string" ? c : "")).toLowerCase() ===
      countryName.toLowerCase()
  );
  const flagSrc =
    match?.flag ||
    (match?.iso2 ? `https://flagcdn.com/w40/${match.iso2.toLowerCase()}.png` : null);
  if (flagSrc) {
    return (
      <img
        src={flagSrc}
        alt={countryName}
        style={{ width: 18, height: 13, objectFit: "cover", borderRadius: 2 }}
        className="me-1 ms-1 d-inline-block align-middle"
      />
    );
  }
  return null;
};

const CATEGORIES = [
  "All",
  "Agriculture",
  "Construction",
  "Chemicals",
  "Electronics",
  "Textiles",
  "Renewable Energy",
  "Food & Beverage",
];

const MY_STATUS_TABS = [
  { key: "PENDING", label: "Review" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
  { key: "CLOSED", label: "Closed" },
  { key: "CANCELLED", label: "Cancelled" },
];

const MY_QUOTE_STATUS_TABS = [
  { key: "PENDING", label: "Pending Review" },
  { key: "ACCEPTED", label: "Accepted" },
  { key: "REJECTED", label: "Rejected" },
];

const NetworkHubMain: React.FC = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<"all" | "my" | "quotes">("all");
  const [myStatusFilter, setMyStatusFilter] = useState<string>("PENDING");
  const [myQuoteStatusFilter, setMyQuoteStatusFilter] = useState<string>("PENDING");
  const [myRequirementsCount, setMyRequirementsCount] = useState<number>(0);

  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>("All Countries");
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState<boolean>(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState<string>("");
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState<string>("Latest");

  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [requirementToCancel, setRequirementToCancel] = useState<any>(null);
  const [requirementToClose, setRequirementToClose] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [closingReq, setClosingReq] = useState<boolean>(false);
  const [bookmarkedIds, setBookmarkedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("nh_bookmarks");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const token = localStorage.getItem("access_token");
  const isLoggedIn = !!(token && token !== "undefined" && token !== "null" && token.trim() !== "");
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [loginModalMsg, setLoginModalMsg] = useState<string>("Sign in to continue");

  const hasActiveFilters = Boolean(
    (activeCategory && activeCategory !== "All") ||
    (selectedCountry && selectedCountry !== "All Countries") ||
    (searchTerm && searchTerm.trim() !== "")
  );

  useEffect(() => {
    getCountryListAPiCall("")
      .then((data: any) => {
        if (Array.isArray(data)) {
          const formatted = data.map((c: any) => ({
            id: c?.id,
            title: c?.title || c?.name || c?.country_name || "",
            iso2: c?.iso2 || "",
            flag:
              c?.flag ||
              (c?.iso2 ? `https://flagcdn.com/w40/${c.iso2.toLowerCase()}.png` : null),
          }));
          setCountryOptions(formatted);
        }
      })
      .catch((err: any) => {
        console.error("Error fetching countries:", err);
      });
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };
    if (showCountryDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showCountryDropdown]);

  const fetchRequirements = useCallback(
    (pageNo: number = 1, isReset: boolean = false) => {
      if (isReset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params: any = {
        page: pageNo,
        per_page: 4,
        sort_by: sortBy,
      };

      if (activeTab === "my") {
        params.my_requirements = 1;
        if (myStatusFilter && myStatusFilter !== "ALL") {
          params.status = myStatusFilter;
        }
      } else if (activeTab === "quotes") {
        if (myQuoteStatusFilter && myQuoteStatusFilter !== "ALL") {
          params.status = myQuoteStatusFilter;
        }
        if (searchTerm.trim() !== "") {
          params.search = searchTerm.trim();
        }
      } else {
        if (activeCategory && activeCategory !== "All") {
          params.category = activeCategory;
        }
        if (selectedCountry && selectedCountry !== "All Countries") {
          params.country = selectedCountry;
        }
        if (searchTerm.trim() !== "") {
          params.search = searchTerm.trim();
        }
      }

      let apiAction = "network-hub/list";
      if (activeTab === "my") {
        apiAction = "network-hub/my-requirements";
      } else if (activeTab === "quotes") {
        apiAction = "network-hub/my-quotes";
      }

      WebService.getAPI({
        action: apiAction,
        body: params,
        isShowError: false,
      })
        .then((res: any) => {
          if (res?.status || Array.isArray(res?.list)) {
            const rawList = res?.list || res?.data?.list || [];
            const resTotal = res?.total ?? res?.data?.total ?? rawList.length;
            const resLastPage = res?.last_page ?? res?.data?.last_page ?? 1;

            const mapped = rawList.map((item: any) => {
              if (activeTab === "quotes") {
                const req = item.requirement || {};
                const { icon, iconBg } = getCategoryIcon(req.category || req.product_name || "");
                return {
                  ...item,
                  id: item.uuid || item.id,
                  quoteId: item.id || item.uuid,
                  quoteStatus: (item.status || "PENDING").toUpperCase(),
                  unitPrice: item.unit_price || item.offered_unit_price,
                  currency: item.currency || "$",
                  leadTimeDays: item.lead_time_days,
                  offerRemarks: item.offer_remarks,
                  attachedFileUrl: item.attached_file_url,
                  submittedTime: item.created_at ? moment(item.created_at).fromNow() : "Recently",
                  // Associated requirement details
                  requirement: req,
                  reqId: req.uuid || req.id,
                  title: req.product_name || req.title || "Business Requirement",
                  category: req.category || "General",
                  sub_category: req.sub_category || "",
                  description:
                    req.requirement_description ||
                    req.requirment_description ||
                    req.description ||
                    "No additional specifications provided.",
                  quantity: req.qty || req.quantity || "—",
                  country: req.country || "Global",
                  price: req.payment_term || req.price || "",
                  buyerName:
                    req.company_name ||
                    (req.user
                      ? `${req.user.first_name || ""} ${req.user.last_name || ""}`.trim()
                      : "") ||
                    "Verified Buyer",
                  buyerUser: req.user,
                  reqStatus: (req.status || "APPROVED").toUpperCase(),
                  icon,
                  iconBg,
                };
              }

              const { icon, iconBg } = getCategoryIcon(item.category || item.product_name || "");
              const isUrgent =
                (item.priority || "").toUpperCase() === "URGENT" ||
                (item.priority || "").toUpperCase() === "HIGH";
              const isNew = item.created_at
                ? moment().diff(moment(item.created_at), "days") <= 3
                : true;
              return {
                ...item,
                id: item.uuid || item.id,
                title: item.product_name || item.title || "Business Requirement",
                category: item.category || "General",
                description:
                  item.requirement_description ||
                  item.requirment_description ||
                  item.description ||
                  "No additional specifications provided.",
                quantity: item.qty || item.quantity || "Specified quantity",
                country: item.country || "Global",
                price: item.payment_term || item.price || "Negotiable",
                postedTime: item.created_at ? moment(item.created_at).fromNow() : "Recently",
                quotesCount: item.quotes_count ?? (item.quotes ? item.quotes.length : 0),
                icon,
                iconBg,
                buyerName:
                  item.company_name ||
                  (item.user
                    ? `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim()
                    : "") ||
                  "Verified buyer",
                isNew,
                isUrgent,
              };
            });

            setRequirements((prev) => (isReset ? mapped : [...prev, ...mapped]));
            setTotal(resTotal);
            setPage(pageNo);
            setLastPage(resLastPage);
          } else {
            if (isReset) setRequirements([]);
          }
        })
        .catch((err: any) => {
          console.error("Error fetching network hub list:", err);
          if (isReset) setRequirements([]);
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [activeTab, myStatusFilter, myQuoteStatusFilter, activeCategory, selectedCountry, searchTerm, sortBy]
  );

  // Trigger search on filter changes
  useEffect(() => {
    setPage(1);
    fetchRequirements(1, true);
  }, [activeTab, myStatusFilter, myQuoteStatusFilter, activeCategory, selectedCountry, searchTerm, sortBy, fetchRequirements]);

  const handleOpenCloseModal = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setRequirementToClose(item);
  };

  const confirmCloseRequirement = () => {
    if (!requirementToClose) return;
    const itemId = requirementToClose.id || requirementToClose.uuid;
    setClosingReq(true);
    WebService.postAPI({
      action: `network-hub/requirement/${itemId}/status`,
      body: { status: "CLOSED" },
      isShowError: true,
    })
      .then((res: any) => {
        if (res && res.status) {
          toast.success("Requirement closed successfully");
          setRequirements((prev) =>
            prev.map((r) =>
              r.id === itemId || r.uuid === itemId ? { ...r, status: "CLOSED" } : r
            )
          );
          setRequirementToClose(null);
          fetchRequirements(1, true);
        } else {
          toast.error(res?.message || "Failed to close requirement");
        }
      })
      .catch((err: any) => {
        console.error("Error closing requirement:", err);
        toast.error(err?.response?.data?.message || err?.message || "Failed to close requirement");
      })
      .finally(() => {
        setClosingReq(false);
      });
  };

  const handleOpenCancelModal = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setRequirementToCancel(item);
    setCancelReason("");
  };

  const confirmCancelRequirement = () => {
    if (!requirementToCancel) return;
    const itemId = requirementToCancel.id || requirementToCancel.uuid;
    setCancelling(true);
    WebService.postAPI({
      action: `network-hub/requirement/${itemId}/cancel`,
      body: { reason_for_cancel: cancelReason.trim() || "Cancelled by buyer" },
      isShowError: true,
    })
      .then((res: any) => {
        if (res && res.status) {
          toast.success("Requirement cancelled successfully");
          setRequirements((prev) =>
            prev.map((r) =>
              r.id === itemId || r.uuid === itemId ? { ...r, status: "CANCELLED" } : r
            )
          );
          setRequirementToCancel(null);
          setCancelReason("");
          fetchRequirements(1, true);
        } else {
          toast.error(res?.message || "Failed to cancel requirement");
        }
      })
      .catch((err: any) => {
        console.error("Error cancelling requirement:", err);
        toast.error(err?.response?.data?.message || err?.message || "Failed to cancel requirement");
      })
      .finally(() => {
        setCancelling(false);
      });
  };

  // Debounced search term updater
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Infinite scroll trigger via IntersectionObserver & scroll container
  useEffect(() => {
    const currentTarget = observerTarget.current;
    const scrollContainer = scrollContainerRef.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && page < lastPage) {
          fetchRequirements(page + 1, false);
        }
      },
      {
        root: scrollContainer || null,
        rootMargin: "250px",
        threshold: 0.1,
      }
    );

    observer.observe(currentTarget);

    return () => {
      observer.disconnect();
    };
  }, [loading, loadingMore, page, lastPage, fetchRequirements]);

  const handleContainerScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 250) {
      if (!loading && !loadingMore && page < lastPage) {
        fetchRequirements(page + 1, false);
      }
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSearchTerm(searchInput);
  };

  const toggleBookmark = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      setLoginModalMsg("You need to be logged in to bookmark requirements in Network Hub.");
      setShowLoginModal(true);
      return;
    }
    setBookmarkedIds((prev) => {
      const exists = prev.includes(id);
      const updated = exists ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        localStorage.setItem("nh_bookmarks", JSON.stringify(updated));
      } catch (err) {
        console.error("Error saving bookmark:", err);
      }
      return updated;
    });
  };

  const handleViewDetails = (item: any) => {
    if (!isLoggedIn) {
      setLoginModalMsg(
        "If you are already registered, please Log In. Otherwise, please Register to view full business requirement specifications, attached documents, and buyer details."
      );
      setShowLoginModal(true);
      return;
    }
    const targetId = item.uuid || item.id;
    if (activeTab === "my") {
      navigate(`/network-hub/my-requirement/${targetId}`);
    } else if (activeTab === "quotes") {
      navigate(`/network-hub/requirement/${item.reqId || targetId}`);
    } else {
      navigate(`/network-hub/requirement/${targetId}`);
    }
  };

  const remainingCount = Math.max(0, total - requirements.length);

  const activeCountryObj = countryOptions.find(
    (c: any) =>
      (c?.title || c?.name || c?.country_name || (typeof c === "string" ? c : "")).toLowerCase() ===
      selectedCountry.toLowerCase()
  );
  const activeFlag =
    selectedCountry !== "All Countries" && activeCountryObj
      ? activeCountryObj.flag ||
      (activeCountryObj.iso2
        ? `https://flagcdn.com/w40/${activeCountryObj.iso2.toLowerCase()}.png`
        : null)
      : null;

  const filteredCountries = countryOptions.filter((c: any) => {
    const title = c?.title || c?.name || c?.country_name || "";
    return title.toLowerCase().includes(countrySearchQuery.toLowerCase());
  });

  return (
    <section className="investor-page">
      <Container>
        {/* Head Section matching website layout */}
        {!isLoggedIn ? (
          <div
            className="guest-forum-header d-flex align-items-center justify-content-between flex-wrap py-2 px-3 px-md-4 mb-3 rounded-3"
            style={{
              background: "#ffffff",
              border: "1px solid #e8ece9",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
              minHeight: "60px",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <Link to="/" className="d-inline-flex align-items-center">
                <img
                  src={Logo}
                  alt="The Prime Shark"
                  height={34}
                  style={{ objectFit: "contain" }}
                />
              </Link>
              <div className="d-none d-md-block border-start border-secondary border-opacity-25 ps-3 ms-1">
                <h2 className="fw-bold mb-0 fs-6" style={{ color: "#1f2933" }}>
                  Network Hub
                </h2>
                <p className="mb-0 text-muted small" style={{ fontSize: "12px" }}>
                  Discover verified business requirements & connect with global buyers & suppliers.
                </p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
              <Link to="/" className="guest-header-btn-login">
                Log In
              </Link>
              <Link to="/" className="guest-header-btn-join">
                Join Now
              </Link>
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between mt-2 mb-3">
            <h4 className="font-bold mb-0 d-flex align-items-center gap-2 font-18">
              <BackButton />
              Network Hub
            </h4>
          </div>
        )}

        {/* Hero Banner */}
        <div className="nh-hero-banner mb-3">
          <Row className="align-items-center w-100 m-0">
            <Col lg={8} md={12} className="p-0">
              <div className="nh-live-marketplace-badge">
                <span className="dot-live"></span>
                <span>Live marketplace</span>
              </div>
              <h1 className="nh-hero-h1">
                Connect with Global <span className="highlight-green">Buyers & Suppliers</span>
              </h1>
              <p className="nh-hero-p">
                Discover verified business requirements & connect with global buyers & suppliers. Post requirements and receive quotes within 48 hours.
              </p>

              {/* Search Control Group */}
              <form onSubmit={handleSearchSubmit} className="nh-hero-search-group mb-2">
                <div className="search-input-wrap">
                  <FaSearch className="text-white opacity-50" />
                  <input
                    type="text"
                    placeholder="Search product, category, company, or keyword..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="btn btn-link text-white opacity-75 p-0 me-2"
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                      }}
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
                <div className="country-select-wrap" ref={countryDropdownRef}>
                  <button
                    type="button"
                    className="country-picker-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowCountryDropdown((prev) => !prev);
                    }}
                  >
                    {activeFlag ? (
                      <img src={activeFlag} alt={selectedCountry} className="selected-flag-img" />
                    ) : (
                      <FaGlobe className="text-white opacity-75 flex-shrink-0" size={13} />
                    )}
                    <span className="country-label">{selectedCountry}</span>
                    <FaChevronDown className="dropdown-chevron" />
                  </button>

                  {showCountryDropdown && (
                    <div className="country-dropdown-menu">
                      <div className="country-search-box">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={countrySearchQuery}
                          onChange={(e) => setCountrySearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="country-items-list">
                        <div
                          className={`country-item-option ${selectedCountry === "All Countries" ? "active" : ""}`}
                          onClick={() => {
                            setSelectedCountry("All Countries");
                            setShowCountryDropdown(false);
                            setCountrySearchQuery("");
                          }}
                        >
                          <FaGlobe className="text-secondary flex-shrink-0" size={15} />
                          <span className="flex-grow-1 text-truncate">All Countries</span>
                          {selectedCountry === "All Countries" && (
                            <FaCheck className="text-success ms-auto font-11" />
                          )}
                        </div>
                        {filteredCountries.map((c: any, idx: number) => {
                          const name = c?.title || c?.name || c?.country_name || "";
                          const flagUrl =
                            c?.flag ||
                            (c?.iso2
                              ? `https://flagcdn.com/w40/${c.iso2.toLowerCase()}.png`
                              : null);
                          const isSelected =
                            selectedCountry.toLowerCase() === name.toLowerCase();
                          return (
                            <div
                              key={c?.id || idx}
                              className={`country-item-option ${isSelected ? "active" : ""}`}
                              onClick={() => {
                                setSelectedCountry(name);
                                setShowCountryDropdown(false);
                                setCountrySearchQuery("");
                              }}
                            >
                              {flagUrl ? (
                                <img src={flagUrl} alt={name} />
                              ) : (
                                <FaGlobe className="text-secondary flex-shrink-0" size={15} />
                              )}
                              <span className="flex-grow-1 text-truncate">{name}</span>
                              {isSelected && (
                                <FaCheck className="text-success ms-auto font-11" />
                              )}
                            </div>
                          );
                        })}
                        {filteredCountries.length === 0 && (
                          <div className="p-3 text-center text-muted font-12">
                            No countries found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button type="submit" className="btn-search-amber">
                  Search
                </button>
              </form>

              {/* Quick Links */}
              <div className="nh-hero-quick-links">
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    setActiveCategory("All");
                    setSelectedCountry("All Countries");
                    setSearchInput("");
                    setSearchTerm("");
                  }}
                >
                  Browse all requirements →
                </span>
                <span
                  className="cursor-pointer"
                  onClick={() => navigate("/network-hub/post-requirement")}
                >
                  Post a requirement
                </span>
              </div>
            </Col>

            <Col lg={4} md={12} className="d-none d-lg-block text-center">
              <div className="nh-radar-graphic">
                <div className="radar-node node-1"></div>
                <div className="radar-node node-2"></div>
                <div className="radar-node node-3"></div>
                <div className="radar-node node-4"></div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Stats Strip below Hero */}
        {/* <div className="nh-stats-strip mb-4">
          <div className="stat-item">
            <span>📋</span>
            <strong>{total > 0 ? `${total}+` : "12,500+"}</strong>
            <span>Active Requirements</span>
          </div>
          <div className="stat-item">
            <span>✅</span>
            <strong>8,750+</strong>
            <span>Verified Buyers</span>
          </div>
          <div className="stat-item">
            <span>🌐</span>
            <strong>60+</strong>
            <span>Countries</span>
          </div>
          <div className="stat-item">
            <span>📦</span>
            <strong>25+</strong>
            <span>Categories</span>
          </div>
          <div className="stat-item">
            <span>🛡️</span>
            <span className="fw-semibold text-dark">Verified & secure marketplace</span>
          </div>
        </div> */}

        {/* Categories Pills Bar */}
        {/* <div className="d-flex align-items-center gap-2 overflow-auto pb-2 mb-4 scrollbar-hidden">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`btn btn-sm rounded-pill px-3 py-2 font-13 text-nowrap transition-all ${
                activeCategory === cat
                  ? "btn-success text-white fw-bold shadow-sm"
                  : "btn-outline-secondary text-secondary bg-white"
              }`}
              style={
                activeCategory === cat
                  ? { backgroundColor: "#6fb64c", borderColor: "#6fb64c" }
                  : {}
              }
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div> */}

        {/* Logged in User Tab Switcher: Marketplace vs My Requirements vs My Quotes */}
        {isLoggedIn && (
          <div className="nh-main-nav-tabs d-flex align-items-center gap-2 mb-3 border-bottom pb-3 flex-wrap">
            <button
              className={`btn px-4 py-2 rounded-3 fw-bold font-14 transition-all nh-tab-btn ${activeTab === "all"
                ? "btn-success text-white shadow-sm active"
                : "btn-light text-secondary border"
                }`}
              style={activeTab === "all" ? { backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff" } : {}}
              onClick={() => {
                setActiveTab("all");
                setMyStatusFilter("ALL");
              }}
            >
              Marketplace Feed
            </button>
            <button
              className={`btn px-4 py-2 rounded-3 fw-bold font-14 transition-all d-flex align-items-center gap-2 nh-tab-btn ${activeTab === "my"
                ? "btn-success text-white shadow-sm active"
                : "btn-light text-secondary border"
                }`}
              style={activeTab === "my" ? { backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff" } : {}}
              onClick={() => {
                setActiveTab("my");
                setMyStatusFilter("PENDING");
              }}
            >
              <span>My Requirements</span>
              {/* {activeTab === "my" && total > 0 && (
                <span className="badge bg-warning text-dark font-11 px-2 py-0.5 rounded-pill">
                  {total}
                </span>
              )} */}
            </button>
            <button
              className={`btn px-4 py-2 rounded-3 fw-bold font-14 transition-all d-flex align-items-center gap-2 nh-tab-btn ${activeTab === "quotes"
                ? "btn-success text-white shadow-sm active"
                : "btn-light text-secondary border"
                }`}
              style={activeTab === "quotes" ? { backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff" } : {}}
              onClick={() => {
                setActiveTab("quotes");
                setMyQuoteStatusFilter("PENDING");
              }}
            >
              <span>My Quotes</span>
              {/* {activeTab === "quotes" && total > 0 && (
                <span className="badge bg-warning text-dark font-11 px-2 py-0.5 rounded-pill">
                  {total}
                </span>
              )} */}
            </button>
          </div>
        )}

        {/* Status Sub-Tabs for My Requirements */}
        {isLoggedIn && activeTab === "my" && (
          <div className="nh-status-subtabs d-flex align-items-center gap-2 overflow-auto pb-2 mb-3 scrollbar-hidden">
            {MY_STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`btn btn-sm rounded-pill px-3 py-1.5 font-13 text-nowrap transition-all nh-subtab-pill ${myStatusFilter === tab.key
                  ? "btn-success text-white fw-bold shadow-sm active"
                  : "btn-light text-secondary border"
                  }`}
                style={
                  myStatusFilter === tab.key
                    ? { backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff" }
                    : {}
                }
                onClick={() => setMyStatusFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Status Sub-Tabs for My Quotes */}
        {isLoggedIn && activeTab === "quotes" && (
          <div className="nh-status-subtabs d-flex align-items-center gap-2 overflow-auto pb-2 mb-3 scrollbar-hidden">
            {MY_QUOTE_STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`btn btn-sm rounded-pill px-3 py-1.5 font-13 text-nowrap transition-all nh-subtab-pill ${myQuoteStatusFilter === tab.key
                  ? "btn-success text-white fw-bold shadow-sm active"
                  : "btn-light text-secondary border"
                  }`}
                style={
                  myQuoteStatusFilter === tab.key
                    ? { backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff" }
                    : {}
                }
                onClick={() => setMyQuoteStatusFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Count & Sort Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold text-dark mb-0 font-15">
            {loading
              ? "Loading..."
              : activeTab === "my"
                ? `My Posted Requirements (${total})`
                : activeTab === "quotes"
                  ? `My Submitted Quotes (${total})`
                  : `${total} Requirements Found`}
          </h6>
          <div className="d-flex align-items-center gap-2 font-13 text-muted">
            <span>Sort:</span>
            <select
              className="form-select form-select-sm border-0 bg-transparent fw-semibold text-dark"
              style={{ width: "auto", cursor: "pointer" }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="Latest">Latest ▾</option>
              <option value="Oldest">Oldest</option>
            </select>
          </div>
        </div>

        {/* Main 2-Column Grid */}
        <Row className="g-4 mb-5">
          {/* Left Column: Requirements List (Col lg=8) */}
          <Col lg={8} md={12}>
            {loading ? (
              <div className="bg-white rounded-3 border p-4 text-center">
                <CompoLoader inline defaultHeight="320px" />
              </div>
            ) : requirements.length === 0 ? (
              <div className="text-center py-5 bg-white rounded-3 border">
                <div className="font-40 mb-2">
                  {activeTab === "my"
                    ? "📝"
                    : activeTab === "quotes"
                      ? "💼"
                      : hasActiveFilters
                        ? "🔍"
                        : "📦"}
                </div>
                <h5 className="fw-bold text-dark">
                  {activeTab === "my"
                    ? "No requirements found in this status"
                    : activeTab === "quotes"
                      ? "No quotes submitted yet"
                      : hasActiveFilters
                        ? "No matching requirements"
                        : "No requirements available"}
                </h5>
                <p className="text-muted font-13 mb-3">
                  {activeTab === "my"
                    ? "Post your business requirements to receive quotations from verified global suppliers."
                    : activeTab === "quotes"
                      ? "Browse the Marketplace Feed to discover business requirements and submit your price quotations."
                      : hasActiveFilters
                        ? "Try adjusting your search filters or browse other categories."
                        : "There are currently no business requirements posted in the marketplace. Check back soon or post a requirement."}
                </p>
                {activeTab === "my" ? (
                  <button
                    className="btn btn-success btn-sm px-4 py-2 fw-bold font-13 text-white shadow-xs"
                    style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c" }}
                    onClick={() => navigate("/network-hub/post-requirement")}
                  >
                    + Post Your Requirement
                  </button>
                ) : activeTab === "quotes" ? (
                  <button
                    className="btn btn-success btn-sm px-4 py-2 fw-bold font-13 text-white shadow-xs"
                    style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c" }}
                    onClick={() => setActiveTab("all")}
                  >
                    Browse Marketplace Feed
                  </button>
                ) : hasActiveFilters ? (
                  <button
                    className="btn btn-outline-success btn-sm px-3"
                    onClick={() => {
                      setActiveCategory("All");
                      setSelectedCountry("All Countries");
                      setSearchInput("");
                      setSearchTerm("");
                    }}
                  >
                    Clear All Filters
                  </button>
                ) : (
                  <button
                    className="btn btn-success btn-sm px-4 py-2 fw-bold font-13 text-white shadow-xs"
                    style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c" }}
                    onClick={() => navigate("/network-hub/post-requirement")}
                  >
                    + Post Your Requirement
                  </button>
                )}
              </div>
            ) : (
              <div
                ref={scrollContainerRef}
                onScroll={handleContainerScroll}
                className="nh-requirements-scroll-container d-flex flex-column gap-3"
              >
                {requirements.map((item) => {
                  const itemId = item.id || item.uuid;
                  const isBookmarked = bookmarkedIds.includes(itemId);

                  if (activeTab === "quotes") {
                    return (
                      <div key={itemId} className="nh-req-card">
                        {/* Top Row: Badges & Quote Status */}
                        <div className="card-top-row mb-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="badge bg-secondary bg-opacity-10 text-dark border font-11 px-2.5 py-1">
                              {item.category}
                            </span>
                            {item.sub_category && (
                              <span className="badge bg-light text-muted border font-11 px-2.5 py-1">
                                {item.sub_category}
                              </span>
                            )}
                          </div>

                          <div>
                            {item.quoteStatus === "ACCEPTED" ? (
                              <span className="badge bg-success font-12 px-3 py-1.5 rounded-pill d-inline-flex align-items-center gap-1 text-white shadow-xs">
                                <FaCheck size={11} /> Accepted
                              </span>
                            ) : item.quoteStatus === "REJECTED" ? (
                              <span className="badge bg-danger font-12 px-3 py-1.5 rounded-pill d-inline-flex align-items-center gap-1 text-white">
                                <FaTimes size={11} /> Rejected
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark font-12 px-3 py-1.5 rounded-pill border border-warning">
                                ● Pending Review
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Title */}
                        <h5
                          className="card-title-text cursor-pointer mb-3"
                          onClick={() => handleViewDetails(item)}
                        >
                          {item.title}
                        </h5>

                        {/* My Proposal Summary Box */}
                        <div
                          className="rounded-3 border d-flex align-items-center gap-4 flex-wrap font-13 my-3"
                          style={{ padding: "14px 18px", backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}
                        >
                          <div>
                            <span className="text-muted font-11 d-block text-uppercase fw-semibold mb-1" style={{ letterSpacing: "0.5px" }}>
                              My Offered Unit Price
                            </span>
                            <strong className="text-success font-18 d-block">
                              {item.currency} {item.unitPrice || "—"}
                            </strong>
                          </div>
                          {item.leadTimeDays && (
                            <div>
                              <span className="text-muted font-11 d-block text-uppercase fw-semibold mb-1" style={{ letterSpacing: "0.5px" }}>
                                Offered Lead Time
                              </span>
                              <strong className="text-dark font-16 d-block">{item.leadTimeDays} Days</strong>
                            </div>
                          )}
                          <div>
                            <span className="text-muted font-11 d-block text-uppercase fw-semibold mb-1" style={{ letterSpacing: "0.5px" }}>
                              Requested Qty
                            </span>
                            <strong className="text-dark font-16 d-block">{item.quantity}</strong>
                          </div>
                          <div>
                            <span className="text-muted font-11 d-block text-uppercase fw-semibold mb-1" style={{ letterSpacing: "0.5px" }}>
                              Buyer Country
                            </span>
                            <div className="d-flex align-items-center gap-1 font-14 fw-semibold text-dark">
                              {getCountryFlag(item.country, countryOptions)}
                              <span>{item.country}</span>
                            </div>
                          </div>
                          {item.attachedFileUrl && (
                            <div className="ms-auto">
                              <a
                                href={item.attachedFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-success py-1.5 px-3 font-12 fw-semibold d-inline-flex align-items-center gap-1.5 rounded-2 shadow-xs"
                              >
                                <FaFileDownload size={12} /> Download PDF
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Offer Remarks */}
                        {item.offerRemarks && (
                          <div
                            className="rounded-3 border font-13 text-secondary mb-3"
                            style={{ padding: "12px 16px", backgroundColor: "#ffffff", borderColor: "#e2e8f0" }}
                          >
                            <strong className="text-dark font-12 d-block mb-1">My Offer Remarks:</strong>
                            <p className="mb-0 text-muted" style={{ lineHeight: "1.55" }}>{item.offerRemarks}</p>
                          </div>
                        )}

                        {/* Bottom Bar */}
                        <div className="card-bottom-bar mt-3 pt-3 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <span className="posted-text font-12 text-muted">
                            Submitted {item.submittedTime} · Buyer: <strong className="text-dark">{item.buyerName}</strong>
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              className="btn-view-details"
                              onClick={() => handleViewDetails(item)}
                            >
                              View Requirement Details
                            </button>
                            {item.quoteStatus === "ACCEPTED" && item.buyerUser?.email && (
                              <a
                                href={`mailto:${item.buyerUser.email}?subject=Quotation for ${item.title}`}
                                className="btn btn-sm btn-success font-12 fw-semibold px-3 py-1.5 rounded-2 text-white shadow-xs"
                                style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c" }}
                              >
                                ✉ Contact Buyer
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={itemId} className="nh-req-card">
                      {/* Top Row Badges + Bookmark */}
                      <div className="card-top-row">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {/* <div
                            className="avatar-icon-box me-1"
                            style={{ backgroundColor: item.iconBg }}
                          >
                            {item.icon}
                          </div> */}
                          {item.isNew && (
                            <span className="badge bg-success bg-opacity-10 text-success font-11 px-2 py-1">
                              NEW
                            </span>
                          )}
                          {item.isUrgent && (
                            <span className="badge bg-danger bg-opacity-10 text-danger font-11 px-2 py-1">
                              URGENT
                            </span>
                          )}
                          <span className="badge bg-secondary bg-opacity-10 text-dark border font-11 px-2 py-1">
                            {item.category}
                          </span>
                          {item.sub_category && (
                            <span className="badge bg-light text-muted border font-11 px-2 py-1">
                              {item.sub_category}
                            </span>
                          )}
                        </div>

                        {/* <button
                          className="btn btn-link text-muted p-0"
                          onClick={(e) => toggleBookmark(itemId, e)}
                          title={isBookmarked ? "Bookmarked" : "Bookmark"}
                        >
                          {isBookmarked ? (
                            <FaBookmark className="text-warning font-16" />
                          ) : (
                            <FaRegBookmark className="font-16" />
                          )}
                        </button> */}
                      </div>

                      {/* Title */}
                      <h5
                        className="card-title-text cursor-pointer"
                        onClick={() => handleViewDetails(item)}
                      >
                        {item.title}
                      </h5>

                      {/* Description */}
                      <p className="card-desc-text">{item.description}</p>

                      {/* Meta Pills */}
                      <div className="meta-pill-group">
                        <div className="meta-pill d-inline-flex align-items-center">
                          <FaBoxes className="text-success me-1.5 font-13" />
                          <span>{item.quantity}</span>
                        </div>
                        <div className="meta-pill">
                          <FaMapMarkerAlt className="text-danger me-1" />
                          {getCountryFlag(item.country, countryOptions)}
                          <span>{item.country}</span>
                        </div>
                        {item.price && (
                          <div className="meta-pill" title={item.price}>
                            <FaRupeeSign className="text-success flex-shrink-0" />
                            <span className="text-truncate" style={{ maxWidth: "260px" }}>{item.price}</span>
                          </div>
                        )}
                        {activeTab === "my" && (
                          <div
                            className="meta-pill d-inline-flex align-items-center"
                            style={{ backgroundColor: "#edf7e8", borderColor: "#cde4bc", color: "#599b3a" }}
                          >
                            <FaClipboardList className="me-1.5 font-12 text-success" />
                            <strong>
                              {item.quotesCount || 0} {item.quotesCount === 1 ? "Proposal" : "Proposals"}
                            </strong>
                          </div>
                        )}
                      </div>

                      {/* Rejection Reason Notice */}
                      {activeTab === "my" && item.status === "REJECTED" && (
                        <div
                          className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-2 font-12 my-1"
                          style={{
                            backgroundColor: "#fef2f2",
                            color: "#991b1b",
                            width: "fit-content",
                            maxWidth: "100%",
                          }}
                        >
                          <FaTimesCircle className="text-danger flex-shrink-0" size={13} />
                          <span>
                            <strong className="text-danger">Reason for Rejection:</strong>{" "}
                            <span style={{ color: "#374151" }}>
                              {item.reason_for_reject ||
                                item.reject_reason ||
                                item.rejection_reason ||
                                item.admin_remarks ||
                                "This requirement did not meet the marketplace publishing guidelines."}
                            </span>
                          </span>
                        </div>
                      )}

                      {/* Bottom Bar */}
                      <div className="card-bottom-bar">
                        <span className="posted-text">
                          Posted {item.postedTime} · {item.buyerName}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            className="btn-view-details"
                            onClick={() => handleViewDetails(item)}
                          >
                            View Details
                          </button>
                          {activeTab === "my" ? (
                            item.status === "CLOSED" ? (
                              <span className="badge bg-secondary font-12 px-3 py-2 rounded-2">
                                Closed
                              </span>
                            ) : item.status === "REJECTED" ? (
                              <span className="badge bg-danger bg-opacity-10 text-danger font-12 px-3 py-2 rounded-2 border border-danger border-opacity-25">
                                Rejected
                              </span>
                            ) : item.status === "CANCELLED" ? (
                              <span className="badge bg-secondary bg-opacity-10 text-secondary font-12 px-3 py-2 rounded-2 border border-secondary border-opacity-25">
                                Cancelled
                              </span>
                            ) : item.status === "PENDING" ? (
                              <button
                                className="btn btn-sm btn-outline-danger font-12 fw-semibold px-2.5 py-1.5 rounded-2"
                                onClick={(e) => handleOpenCancelModal(item, e)}
                              >
                                Cancel
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-secondary font-12 fw-semibold px-2.5 py-1.5 rounded-2"
                                onClick={(e) => handleOpenCloseModal(item, e)}
                              >
                                Close
                              </button>
                            )
                          ) : item.has_quoted || item.quote_submitted_by_me || item.is_quoted ? (
                            <span className="badge bg-warning bg-opacity-25 text-dark fw-bold font-12 px-3 py-2 rounded-2 border border-warning border-opacity-50 d-inline-flex align-items-center gap-1">
                              <FaCheck size={11} className="text-success" /> Quoted
                            </span>
                          ) : (
                            <button
                              className="btn-submit-quote-dark"
                              onClick={() => {
                                if (!isLoggedIn) {
                                  setLoginModalMsg(
                                    "If you are already registered, please Log In. Otherwise, please Register to submit competitive price quotations and connect with verified buyers."
                                  );
                                  setShowLoginModal(true);
                                  return;
                                }
                                setSelectedQuote(item);
                              }}
                            >
                              Submit Quote
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Infinite Scroll Trigger & Loader */}
                <div ref={observerTarget} className="text-center">
                  {loadingMore && (
                    <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-3">
                      <Spinner
                        animation="border"
                        style={{ width: "2rem", height: "2rem", color: "#26a699", borderWidth: "3px" }}
                      />
                      <span className="text-muted font-13 fw-semibold">
                        Loading more requirements...
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>

          {/* Right Column: Sidebar (Col lg=4) */}
          <Col lg={4} md={12}>
            <div className="d-flex flex-column gap-3 position-sticky" style={{ top: "90px" }}>
              {/* Card 1: Post a Requirement Card */}
              <div className="sidebar-post-card shadow-sm">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="font-22">📋</span>
                    <span className="badge bg-white bg-opacity-20 text-dark font-11 px-2 py-1 rounded-pill">
                      100% Free
                    </span>
                  </div>
                  <span className="badge text-white font-11 px-2 py-1 rounded-pill" style={{ backgroundColor: "#66C15E" }}>
                    Fast Quotes
                  </span>
                </div>

                <h5 className="post-card-title font-18 mb-2">Post Your Requirement</h5>
                <p className="post-card-p font-13 mb-3">
                  Reach verified global suppliers and receive competitive quotes within 48 hours directly to your dashboard.
                </p>

                {/* Key Benefits */}
                <div className="d-flex flex-column gap-2 mb-4 font-12 text-white-50">
                  <div className="d-flex align-items-center gap-2">
                    <FaCheckCircle className="text-warning flex-shrink-0" size={13} />
                    <span className="text-white">Verified international suppliers</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaCheckCircle className="text-warning flex-shrink-0" size={13} />
                    <span className="text-white">Custom quotes & direct negotiation</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaCheckCircle className="text-warning flex-shrink-0" size={13} />
                    <span className="text-white">Zero commission · Free posting</span>
                  </div>
                </div>

                <button
                  className="btn-amber-full py-2 mb-2 shadow"
                  onClick={() => navigate("/network-hub/post-requirement")}
                >
                  <span>Post Requirement Free</span>
                  <FaArrowRight size={13} />
                </button>

                {!isLoggedIn && (
                  <button
                    className="btn-translucent-full py-2 mb-2"
                    onClick={() => navigate("/")}
                  >
                    Login to continue
                  </button>
                )}

                <div className="admin-verified-note d-flex align-items-center justify-content-center gap-1 font-11 mt-2">
                  <FaLock size={10} /> Admin-verified buyers & suppliers. No spam.
                </div>
              </div>

              {/* Card 2: Platform at a Glance Card */}
              {/* <div className="sidebar-glance-card shadow-sm">
                <h6 className="glance-title font-14 mb-3">Platform Highlights</h6>
                <div className="glance-grid mb-0">
                  <div className="glance-box">
                    <div className="glance-num">{total > 0 ? `${total}+` : "100+"}</div>
                    <div className="glance-lbl">Active Inquiries</div>
                  </div>
                  <div className="glance-box box-green">
                    <div className="glance-num text-success">8,750+</div>
                    <div className="glance-lbl">Verified Suppliers</div>
                  </div>
                  <div className="glance-box box-blue">
                    <div className="glance-num text-primary">60+</div>
                    <div className="glance-lbl">Countries</div>
                  </div>
                  <div className="glance-box box-amber">
                    <div className="glance-num text-warning">25+</div>
                    <div className="glance-lbl">Categories</div>
                  </div>
                </div>
              </div> */}

              {!isLoggedIn && (
                <div className="sidebar-glance-card shadow-sm">
                  <div className="member-callout-yellow p-3 rounded-3 bg-light border">
                    <div className="callout-head fw-bold text-dark font-13 mb-1">👤 Not a member yet?</div>
                    <div className="callout-p text-muted font-12 mb-3">
                      Register to unlock direct quotes, contact buyers, and post unlimited requirements.
                    </div>
                    <button className="btn btn-success w-100 fw-bold font-13 py-2 rounded-3 text-white shadow-xs" style={{ backgroundColor: "#66C15E", borderColor: "#66C15E" }} onClick={() => navigate("/")}>
                      Register Free →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* How Network Hub Works Section */}
        <div className="nh-how-it-works-card mb-4">
          <div className="text-center mb-4">
            <h4 className="fw-extrabold text-white mb-2 font-22">
              How Network Hub Works for Your Business
            </h4>
            <p className="font-13 opacity-75 max-w-600 mx-auto">
              Simple 4-step process to source products, find buyers, and expand your market footprint globally.
            </p>
          </div>

          <Row className="g-3 align-items-stretch mb-4 position-relative">
            {/* Step 01 */}
            <Col lg={3} md={6} xs={12} className="position-relative">
              <div
                className="p-4 rounded-3 h-100 d-flex flex-column justify-content-between"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                }}
              >
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="font-20 me-2">📝</span>
                    <span className="fw-bold font-15" style={{ color: "#26a699" }}>
                      01
                    </span>
                  </div>
                  <h6 className="fw-bold text-white mb-2 font-15">Post Your Requirement</h6>
                  <p className="font-13 lh-base mb-0" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Fill in the form with product specs, quantity, budget, and delivery window. Takes under 3 minutes.
                  </p>
                </div>
              </div>
              <FaChevronRight
                className="d-none d-lg-block position-absolute"
                style={{
                  right: "-10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#26a699",
                  zIndex: 2,
                  fontSize: "14px",
                }}
              />
            </Col>

            {/* Step 02 */}
            <Col lg={3} md={6} xs={12} className="position-relative">
              <div
                className="p-4 rounded-3 h-100 d-flex flex-column justify-content-between"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                }}
              >
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="font-20 me-2">🔍</span>
                    <span className="fw-bold font-15" style={{ color: "#26a699" }}>
                      02
                    </span>
                  </div>
                  <h6 className="fw-bold text-white mb-2 font-15">Admin Verification</h6>
                  <p className="font-13 lh-base mb-0" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Our team verifies the details to ensure authenticity and keep spam out of the marketplace.
                  </p>
                </div>
              </div>
              <FaChevronRight
                className="d-none d-lg-block position-absolute"
                style={{
                  right: "-10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#26a699",
                  zIndex: 2,
                  fontSize: "14px",
                }}
              />
            </Col>

            {/* Step 03 */}
            <Col lg={3} md={6} xs={12} className="position-relative">
              <div
                className="p-4 rounded-3 h-100 d-flex flex-column justify-content-between"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                }}
              >
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="font-20 me-2">💬</span>
                    <span className="fw-bold font-15" style={{ color: "#26a699" }}>
                      03
                    </span>
                  </div>
                  <h6 className="fw-bold text-white mb-2 font-15">Receive Quotes</h6>
                  <p className="font-13 lh-base mb-0" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Verified suppliers submit quotes with prices, terms, and delivery timelines within 48 hours.
                  </p>
                </div>
              </div>
              <FaChevronRight
                className="d-none d-lg-block position-absolute"
                style={{
                  right: "-10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#26a699",
                  zIndex: 2,
                  fontSize: "14px",
                }}
              />
            </Col>

            {/* Step 04 */}
            <Col lg={3} md={6} xs={12}>
              <div
                className="p-4 rounded-3 h-100 d-flex flex-column justify-content-between"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                }}
              >
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="font-20 me-2">🤝</span>
                    <span className="fw-bold font-15" style={{ color: "#26a699" }}>
                      04
                    </span>
                  </div>
                  <h6 className="fw-bold text-white mb-2 font-15">Connect & Trade</h6>
                  <p className="font-13 lh-base mb-0" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Compare offers, negotiate directly, and close the trade — all within the Network Hub ecosystem.
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Action Button - Only for non-logged in users */}
          {!isLoggedIn && (
            <div className="text-center pt-2">
              <button
                className="btn fw-bold px-4 py-2 font-14 rounded-3 text-white shadow-sm"
                style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c" }}
                onClick={() => navigate("/")}
              >
                Get Started — It's Free
              </button>
            </div>
          )}
        </div>

        {/* Modals */}
        <SubmitQuoteModal
          show={!!selectedQuote}
          onHide={() => setSelectedQuote(null)}
          requirement={selectedQuote}
          onSuccess={() => {
            if (selectedQuote) {
              const qId = selectedQuote.id || selectedQuote.uuid;
              setRequirements((prev) =>
                prev.map((r) =>
                  (r.id === qId || r.uuid === qId)
                    ? { ...r, has_quoted: true, is_quoted: true, quote_submitted_by_me: true }
                    : r
                )
              );
            }
          }}
        />

        {/* Login / Register Required Modal */}
        <Modal
          show={showLoginModal}
          onHide={() => setShowLoginModal(false)}
          centered
          className="nh-auth-modal"
        >
          <div className="nh-auth-modal-header text-center">
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-3"
              onClick={() => setShowLoginModal(false)}
              aria-label="Close"
            />
            <div className="nh-auth-icon-circle">
              <FaLock size={22} />
            </div>
            <h4 className="nh-auth-title">Authentication Required</h4>
            <p className="nh-auth-subtitle">
              {loginModalMsg || "If you are already registered, please Log In. Otherwise, please Register to access requirement specifications, connect with verified buyers, and place quotations."}
            </p>
          </div>

          <Modal.Body className="px-4 py-3 text-center">
            <div className="nh-auth-perks-box">
              <div className="perk-item">
                <FaCheckCircle className="perk-icon" />
                <span>Access verified requirement specifications & RFP files</span>
              </div>
              <div className="perk-item">
                <FaCheckCircle className="perk-icon" />
                <span>Submit competitive price quotations & lead times</span>
              </div>
              <div className="perk-item">
                <FaCheckCircle className="perk-icon" />
                <span>Direct contact & negotiation with verified buyers</span>
              </div>
            </div>

            <div className="nh-auth-action-grid">
              <button
                type="button"
                className="btn-auth-login"
                onClick={() => navigate("/")}
              >
                Log In Now →
              </button>
              <button
                type="button"
                className="btn-auth-register"
                onClick={() => navigate("/")}
              >
                Register Free +
              </button>
            </div>

            <button
              type="button"
              className="nh-auth-dismiss"
              onClick={() => setShowLoginModal(false)}
            >
              Continue Browsing as Guest
            </button>
          </Modal.Body>
        </Modal>
        {/* Cancel Requirement Confirmation Modal */}
        <Modal
          show={!!requirementToCancel}
          onHide={() => {
            if (!cancelling) {
              setRequirementToCancel(null);
              setCancelReason("");
            }
          }}
          centered
        >
          <Modal.Header closeButton={!cancelling} style={{ borderBottom: "1px solid #f1f5f9" }}>
            <Modal.Title className="font-16 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="p-2 rounded-circle bg-danger bg-opacity-10 text-danger font-14 d-inline-flex align-items-center justify-content-center"
                style={{ width: 32, height: 32 }}
              >
                <FaExclamationTriangle size={14} />
              </span>
              <span>Cancel Requirement</span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="p-4">
            <p className="font-14 text-secondary mb-3">
              Are you sure you want to cancel <strong>"{requirementToCancel?.title || requirementToCancel?.product_name || "this requirement"}"</strong>?
            </p>
            <div className="p-3 bg-light rounded-3 font-12 text-muted mb-3 border">
              ⚠️ <strong>Note:</strong> Once cancelled, this requirement will be moved to the <em>Cancelled</em> tab and suppliers will no longer be able to submit new quotations.
            </div>

            <div className="mb-2">
              <label className="form-label font-13 fw-semibold text-dark mb-1">
                Reason for cancellation <span className="text-muted font-12">(Optional)</span>
              </label>

              {/* Quick Select Reason Tags */}
              <div className="d-flex flex-wrap gap-1.5 mb-2">
                {[
                  "Procurement completed elsewhere",
                  "Requirement specifications changed",
                  "Budget constraints",
                  "Posted by mistake",
                ].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`btn btn-sm font-11 px-2.5 py-1 rounded-pill ${cancelReason === tag
                      ? "btn-danger text-white"
                      : "btn-outline-secondary bg-white text-secondary"
                      }`}
                    onClick={() => setCancelReason(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              <textarea
                className="form-control font-13"
                rows={2}
                placeholder="Or type a custom reason..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                disabled={cancelling}
              />
            </div>
          </Modal.Body>

          <Modal.Footer style={{ borderTop: "1px solid #f1f5f9" }}>
            <Button
              variant="outline-secondary"
              size="sm"
              className="px-3.5 py-2 font-13 fw-semibold rounded-2"
              disabled={cancelling}
              onClick={() => {
                setRequirementToCancel(null);
                setCancelReason("");
              }}
            >
              Keep Requirement
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="px-4 py-2 font-13 fw-bold rounded-2 shadow-xs d-inline-flex align-items-center gap-1.5"
              disabled={cancelling}
              onClick={confirmCancelRequirement}
            >
              {cancelling ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Cancelling...</span>
                </>
              ) : (
                <>
                  <FaTimesCircle size={13} />
                  <span>Confirm Cancellation</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* CLOSE REQUIREMENT CONFIRMATION MODAL */}
        <Modal
          show={!!requirementToClose}
          onHide={() => {
            if (!closingReq) {
              setRequirementToClose(null);
            }
          }}
          centered
        >
          <Modal.Header closeButton={!closingReq} style={{ borderBottom: "1px solid #f1f5f9" }}>
            <Modal.Title className="font-16 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="p-2 rounded-circle bg-success bg-opacity-10 text-success font-14 d-inline-flex align-items-center justify-content-center"
                style={{ width: 32, height: 32 }}
              >
                <FaBan size={14} className="me-2" />
              </span>
              <span>Close Requirement</span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="p-4">
            <p className="font-14 text-secondary mb-3">
              Are you sure you want to close <strong>"{requirementToClose?.title || requirementToClose?.product_name || "this requirement"}"</strong>?
            </p>
            <div className="p-3 bg-light rounded-3 font-12 text-muted mb-0 border">
              ℹ️ <strong>Note:</strong> Once closed, suppliers will no longer be able to submit new quotes for this requirement. You will still be able to review all previously received quotes.
            </div>
          </Modal.Body>

          <Modal.Footer style={{ borderTop: "1px solid #f1f5f9" }}>
            <Button
              variant="outline-secondary"
              size="sm"
              className="px-3.5 py-2 font-13 fw-semibold rounded-2"
              disabled={closingReq}
              onClick={() => setRequirementToClose(null)}
            >
              Keep Active
            </Button>
            <Button
              variant="success"
              size="sm"
              className="px-4 py-2 font-13 fw-bold rounded-2 shadow-xs d-inline-flex align-items-center justify-content-center text-white"
              style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff" }}
              disabled={closingReq}
              onClick={confirmCloseRequirement}
            >
              {closingReq ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Closing...</span>
                </>
              ) : (
                <>
                  <FaTimesCircle size={13} className="me-2" />
                  <span>Confirm Close</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </section>
  );
};

export default NetworkHubMain;
